import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SchemaMarkupProps {
    type: 'organization' | 'website' | 'breadcrumb' | 'product' | 'contactPage';
    data?: any;
}

/**
 * SchemaMarkup Component
 * 
 * Adds JSON-LD structured data to pages for enhanced SEO and rich search results.
 * Supports Organization, Website, Breadcrumb, Product, and ContactPage schema types.
 * 
 * Usage:
 * <SchemaMarkup type="organization" />
 * <SchemaMarkup type="website" />
 * <SchemaMarkup type="contactPage" />
 * <SchemaMarkup type="breadcrumb" data={{ items: [...] }} />
 */
const SchemaMarkup: React.FC<SchemaMarkupProps> = ({ type, data = {} }) => {
    const getSchema = () => {
        switch (type) {
            case 'organization':
                return {
                    '@context': 'https://schema.org',
                    '@type': 'Organization',
                    name: 'Point Sails',
                    url: 'https://pointsails.com',
                    logo: 'https://pointsails.com/images/point-sails-logo.png',
                    description: 'Custom replacement sails for sailboats. 30+ years experience in sail design and manufacturing.',
                    foundingDate: '1991',
                    sameAs: [
                        // Add social media profiles when available
                        // 'https://www.facebook.com/pointsails',
                        // 'https://www.instagram.com/pointsails'
                    ],
                    contactPoint: {
                        '@type': 'ContactPoint',
                        contactType: 'Customer Service',
                        telephone: '+1-619-663-9330',
                        email: 'crew@pointsails.com',
                        availableLanguage: 'English'
                    },
                    address: {
                        '@type': 'PostalAddress',
                        addressCountry: 'US'
                    }
                };

            case 'website':
                return {
                    '@context': 'https://schema.org',
                    '@type': 'WebSite',
                    name: 'Point Sails',
                    url: 'https://pointsails.com',
                    description: 'Custom replacement sails for sailboats. Get instant quotes online.',
                    potentialAction: {
                        '@type': 'SearchAction',
                        target: {
                            '@type': 'EntryPoint',
                            urlTemplate: 'https://pointsails.com/quoter?boat={search_term_string}'
                        },
                        'query-input': 'required name=search_term_string'
                    }
                };

            case 'contactPage':
                return {
                    '@context': 'https://schema.org',
                    '@type': 'ContactPage',
                    name: 'Contact Point Sails',
                    description: 'Contact Point Sails for expert advice on custom replacement sails',
                    url: 'https://pointsails.com/contact',
                    mainEntity: {
                        '@type': 'Organization',
                        name: 'Point Sails',
                        telephone: '+1-619-663-9330',
                        email: 'crew@pointsails.com',
                        contactPoint: {
                            '@type': 'ContactPoint',
                            contactType: 'Customer Service',
                            telephone: '+1-619-663-9330',
                            email: 'crew@pointsails.com',
                            availableLanguage: 'English'
                        }
                    }
                };

            case 'breadcrumb':
                // Breadcrumb schema requires custom data
                if (!data.items || data.items.length === 0) {
                    return null;
                }
                
                return {
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: data.items.map((item: any, index: number) => ({
                        '@type': 'ListItem',
                        position: index + 1,
                        name: item.name,
                        item: item.url
                    }))
                };

            case 'product':
                // Product schema for boat-specific sail pages
                return {
                    '@context': 'https://schema.org',
                    '@type': 'Product',
                    name: data.name || 'Custom Replacement Sail',
                    description: data.description || 'Custom-made replacement sail for your sailboat',
                    brand: {
                        '@type': 'Brand',
                        name: 'Point Sails'
                    },
                    offers: {
                        '@type': 'AggregateOffer',
                        priceCurrency: 'USD',
                        availability: 'https://schema.org/InStock',
                        seller: {
                            '@type': 'Organization',
                            name: 'Point Sails'
                        }
                    }
                };

            default:
                return null;
        }
    };

    const schema = getSchema();

    if (!schema) {
        return null;
    }

    return (
        <Helmet>
            <script type="application/ld+json">
                {JSON.stringify(schema)}
            </script>
        </Helmet>
    );
};

export default SchemaMarkup;
